// A read-only coverage matrix over the campaign's Selection Process: rows are
// capability dimensions, columns are interview-flavoured steps, cells show the
// depth from each step's current Step Assessment Plan. The matrix is derived
// server-side (Campaign.selectionProcessCoverage) so this component is purely
// presentational — it never owns the projection.
//
// Two presentations share the same data: a desktop <table> grid on md+ and a
// stacked per-step card list on narrow viewports. Both render labelled chips
// for the depth so the picture survives a colour-blind reader, and surface a
// "Coverage gaps" summary for dimensions covered by no step. Story #2271
// (epic #2268 Phase 6).

export type CoverageDepth = "sample" | "probe" | "deep_dive";

export type CoverageDimension = {
  continuityId: string;
  name: string;
  position: number;
  totalDepth: number;
  coveredStepCount: number;
  uncovered: boolean;
};

export type CoverageStep = {
  id: string;
  continuityId: string;
  position: number;
  label: string;
  stepType: string;
  stepTypeLabel: string;
  dimensionsCovered: number;
  averageDepth: number | null;
  planAcknowledgedAt: string | null;
};

export type CoverageCell = {
  dimensionContinuityId: string;
  stepContinuityId: string;
  depth: CoverageDepth | null;
};

export type CoverageMatrixProps = {
  dimensions: CoverageDimension[];
  steps: CoverageStep[];
  cells: CoverageCell[];
};

const DEPTH_LABEL: Record<CoverageDepth, string> = {
  sample: "Screen",
  probe: "Probe",
  deep_dive: "Deep dive",
};

// Chip palette deliberately scales lightness with depth so the visual signal
// is unambiguous even when the labels are absorbed by squinting. Dark-mode
// twin classes follow the existing tailwind convention used elsewhere in the
// campaigns/* tree.
const DEPTH_CHIP: Record<CoverageDepth, string> = {
  sample:
    "bg-sky-50 text-sky-800 ring-sky-200 dark:bg-sky-900/30 dark:text-sky-200 dark:ring-sky-800",
  probe:
    "bg-sky-100 text-sky-900 ring-sky-300 dark:bg-sky-800/40 dark:text-sky-100 dark:ring-sky-700",
  deep_dive:
    "bg-sky-200 text-sky-900 ring-sky-400 dark:bg-sky-700/60 dark:text-sky-50 dark:ring-sky-600",
};

function DepthChip({ depth }: { depth: CoverageDepth }) {
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ring-1 ${DEPTH_CHIP[depth]}`}
      data-testid={`coverage-chip-${depth}`}
    >
      {DEPTH_LABEL[depth]}
    </span>
  );
}

function formatAverage(value: number | null): string {
  return value === null ? "—" : value.toFixed(1);
}

export function CoverageMatrix({ dimensions, steps, cells }: CoverageMatrixProps) {
  if (dimensions.length === 0 || steps.length === 0) {
    return (
      <section className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
        <h3 className="text-sm font-semibold text-text-primary">Coverage matrix</h3>
        <p className="mt-1 text-xs text-text-muted">
          The matrix lights up once the role blueprint defines capability dimensions and the process
          has at least one interview-flavoured step with a plan.
        </p>
      </section>
    );
  }

  // O(1) cell lookup keyed by `${dim}|${step}`. The server returns a dense
  // list so every (dim × step) pair is present — the map just makes ordered
  // rendering trivial.
  const cellByKey = new Map<string, CoverageCell>();
  for (const c of cells) {
    cellByKey.set(`${c.dimensionContinuityId}|${c.stepContinuityId}`, c);
  }

  const uncoveredDimensions = dimensions.filter((d) => d.uncovered);

  return (
    <section
      className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring"
      data-testid="coverage-matrix"
    >
      <header className="mb-3 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-text-primary">Coverage matrix</h3>
        <p className="text-xs text-text-muted">
          {dimensions.length} dimensions × {steps.length} interview steps
        </p>
      </header>

      {/* Desktop grid */}
      <div className="hidden md:block overflow-x-auto" data-testid="coverage-matrix-desktop">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 bg-card px-2 py-2 text-left text-xs font-medium text-text-muted">
                Dimension
              </th>
              {steps.map((step) => (
                <th
                  key={step.continuityId}
                  scope="col"
                  className="px-2 py-2 text-left text-xs font-medium text-text-muted"
                >
                  <div className="text-text-primary">{step.stepTypeLabel}</div>
                  {step.label !== step.stepTypeLabel && (
                    <div className="text-text-faint">{step.label}</div>
                  )}
                </th>
              ))}
              <th className="px-2 py-2 text-right text-xs font-medium text-text-muted">
                Row total
              </th>
            </tr>
          </thead>
          <tbody>
            {dimensions.map((dim) => (
              <tr key={dim.continuityId} data-testid={`coverage-row-${dim.continuityId}`}>
                <th
                  scope="row"
                  className="sticky left-0 bg-card px-2 py-2 text-left text-sm font-medium text-text-primary"
                >
                  <div className="flex items-center gap-2">
                    <span>{dim.name}</span>
                    {dim.uncovered && (
                      <span
                        className="inline-flex items-center rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium text-rose-700 ring-1 ring-rose-300 dark:bg-rose-900/30 dark:text-rose-300 dark:ring-rose-800"
                        data-testid="coverage-uncovered-pill"
                      >
                        Uncovered
                      </span>
                    )}
                  </div>
                </th>
                {steps.map((step) => {
                  const cell = cellByKey.get(`${dim.continuityId}|${step.continuityId}`);
                  const depth = cell?.depth ?? null;
                  return (
                    <td key={step.continuityId} className="px-2 py-2 align-middle">
                      {depth ? (
                        <DepthChip depth={depth} />
                      ) : (
                        <span className="text-text-faint" role="img" aria-label="not covered">
                          —
                        </span>
                      )}
                    </td>
                  );
                })}
                <td className="px-2 py-2 text-right text-sm tabular-nums text-text-secondary">
                  {dim.totalDepth}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th
                scope="row"
                className="sticky left-0 bg-card px-2 py-2 text-left text-xs font-medium text-text-muted"
              >
                Covered / avg depth
              </th>
              {steps.map((step) => (
                <td
                  key={step.continuityId}
                  className="px-2 py-2 text-left text-xs text-text-secondary"
                >
                  <span className="tabular-nums">{step.dimensionsCovered}</span>
                  <span className="text-text-faint"> · </span>
                  <span className="tabular-nums">{formatAverage(step.averageDepth)}</span>
                </td>
              ))}
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Mobile stacked list */}
      <div className="space-y-3 md:hidden" data-testid="coverage-matrix-mobile">
        {steps.map((step) => {
          const coveredCells = dimensions
            .map((dim) => {
              const cell = cellByKey.get(`${dim.continuityId}|${step.continuityId}`);
              const depth = cell?.depth ?? null;
              return depth ? { dim, depth } : null;
            })
            .filter((x): x is { dim: CoverageDimension; depth: CoverageDepth } => x !== null);

          return (
            <div
              key={step.continuityId}
              className="rounded border border-input-border bg-card p-3"
              data-testid={`coverage-step-card-${step.continuityId}`}
            >
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-sm font-semibold text-text-primary">
                    {step.stepTypeLabel}
                  </div>
                  {step.label !== step.stepTypeLabel && (
                    <div className="text-xs text-text-faint">{step.label}</div>
                  )}
                </div>
                <div className="text-xs text-text-muted">
                  <span className="tabular-nums">{step.dimensionsCovered}</span>
                  <span className="text-text-faint"> · </span>
                  <span className="tabular-nums">{formatAverage(step.averageDepth)}</span>
                </div>
              </div>
              {coveredCells.length === 0 ? (
                <p className="mt-2 text-xs text-text-faint">
                  This step's plan covers no current dimensions.
                </p>
              ) : (
                <ul className="mt-2 space-y-1">
                  {coveredCells.map(({ dim, depth }) => (
                    <li key={dim.continuityId} className="flex items-center justify-between">
                      <span className="text-sm text-text-primary">{dim.name}</span>
                      <DepthChip depth={depth} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}

        {uncoveredDimensions.length > 0 && (
          <div
            className="rounded border border-rose-200 bg-rose-50 p-3 dark:border-rose-800 dark:bg-rose-950"
            data-testid="coverage-gaps-panel"
          >
            <div className="text-xs font-semibold text-rose-800 dark:text-rose-200">
              Coverage gaps
            </div>
            <ul className="mt-1 space-y-0.5">
              {uncoveredDimensions.map((dim) => (
                <li key={dim.continuityId} className="text-sm text-rose-900 dark:text-rose-100">
                  {dim.name}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
